import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, SimpleChanges, OnChanges, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { User } from '@auth0/auth0-spa-js';
import { Guid } from 'guid-typescript';
import { NotificationModel } from 'src/app/models/notification.model';
import { NotificationService } from 'src/app/services/notification.service';
import { NotificationStatusEnum } from 'src/app/shared/enumerators/notification-status.enum';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { UserService } from 'src/app/shared/services/user.service';
import * as signalR from '@microsoft/signalr';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserManagementService } from 'src/app/services/user-management.service';
import { RolesModel } from 'src/app/models/roles.model';
import { MatBadge } from '@angular/material/badge';
import { MatDrawerContainer, MatDrawer } from '@angular/material/sidenav';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NgClass, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    imports: [MatBadge, MatDrawerContainer, MatDrawer, MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem, MatSlideToggle, FormsModule, NgClass, MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, DatePipe]
})
export class NotificationsComponent implements OnInit, OnDestroy, OnChanges {
  private readonly notificationSrv = inject(NotificationService);
  private readonly configSvc = inject(ConfigurationService);
  private readonly usrSvc = inject(UserService);
  private readonly usrManagementService = inject(UserManagementService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  toggleNotificationSidebar = false;
  notificationsList: NotificationModel[] = [];
  notificationsCount = 0;
  showAll = false;
  user?: User | null;

  @Input() otherSidebarIsOpen = false;
  @Output() openNotifiDrawerEvent = new EventEmitter<void>();
  @Output() launchNotificationCenterEvent = new EventEmitter<void>();

  private hubConnection?: signalR.HubConnection;
  private subscriptions: Subscription[] = [];
  private notificationsListAUX: NotificationModel[] = [];
  private listenerRegistered = false;
  isChecked: boolean = true;

  private readonly maxInMemory = 10;

  ngOnInit() {
    this.subscriptions.push(
      this.usrSvc.userDataChanged.subscribe((user) => {
        if (!user) return;
        this.user = user;
        this.getNotifications(user.email!);
        this.initSignalRService();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['otherSidebarIsOpen']?.currentValue) {
      this.toggleNotificationSidebar = false;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.hubConnection?.stop().catch((err) => console.error('Error stopping SignalR:', err));
  }

  private applyFilterToView() {
    if (!this.notificationsListAUX) {
      this.notificationsList = [];
      return;
    }

    this.notificationsList = this.isChecked
      ? this.notificationsListAUX.filter((x) => x.status === NotificationStatusEnum.Sent)
      : [...this.notificationsListAUX]; // Toggle off = every notification, read and unread (it used to show read only).
  }

  private async initSignalRService() {
    if (this.listenerRegistered) return;

    this.hubConnection = new signalR.HubConnectionBuilder()
      // The hub requires the Auth0 access token. Long polling sends it in the Authorization header: WebSockets and
      // Server-Sent Events would put the ~3.7 KB token in the URL, which the gateway rejects (403).
      .withUrl(this.configSvc.notificationApiUrl + 'hubs/notifications', {
        accessTokenFactory: () => firstValueFrom(this.auth.getAccessTokenSilently()),
        transport: signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.hubConnection.onreconnected(() => {
      this.joinUserAndRoles().catch(err => console.error('Error re-uniendo a grupos:', err));
    });

    this.hubConnection.onreconnecting((error) => console.warn('SignalR reconectando:', error));
    this.hubConnection.onclose((error) => console.warn('SignalR cerrado:', error));

    this.hubConnection.on('NotificationChannel', (message: NotificationModel) => {
      if (!message) return;

      this.notificationsListAUX.unshift({ ...message, status: NotificationStatusEnum.Sent });
      if (this.notificationsListAUX.length > this.maxInMemory) this.notificationsListAUX.pop();

      this.notificationsCount = this.notificationsListAUX.filter(n => n.status === NotificationStatusEnum.Sent).length;
      this.applyFilterToView();
    });

    try {
      await this.hubConnection.start();
      console.log('SignalR conectado');

      await this.joinUserAndRoles();

      this.listenerRegistered = true;
    } catch (err) {
      console.error('Error iniciando SignalR:', err);
    }
  }

  private async joinUserAndRoles() {
    if (!this.user) return;

    await this.hubConnection?.invoke('JoinUser', this.user.email);

    const usrRoles: RolesModel[] = await firstValueFrom(
      this.usrManagementService.getAllUserRole(this.user.sub!)
    );

    if (usrRoles?.length) {
      for (const role of usrRoles) {
        await this.hubConnection?.invoke('JoinRole', role.id + this.user.email);
      }
    }
  }

  private getNotifications(email: string, onlyUnread = false) {
    const sub = this.notificationSrv.getAllNotificationsByUser(email, 7, true)
      .subscribe((data) => {
        this.notificationsListAUX = data ?? [];

        this.notificationsCount = this.notificationsListAUX.filter(n => n.status === NotificationStatusEnum.Sent).length;

        this.applyFilterToView();
      });
    this.subscriptions.push(sub);
  }

  refreshNotifications() {
    if (this.user?.email) this.getNotifications(this.user.email);
  }

  markAllAsRead(refresh = false) {
    const unreadInAUX = this.notificationsListAUX.filter(n => n.status === NotificationStatusEnum.Sent);
    if (!unreadInAUX.length) return;

    const sub = this.notificationSrv.markAllAsRead(unreadInAUX).subscribe(() => {
      unreadInAUX.forEach(n => {
        const auxItem = this.notificationsListAUX.find(x => x.id === n.id);
        if (auxItem) auxItem.status = NotificationStatusEnum.Read;
      });

      this.notificationsCount = this.notificationsListAUX.filter(n => n.status === NotificationStatusEnum.Sent).length;

      this.showOnlyUnread({ checked: this.isChecked } as MatSlideToggleChange);

      if (refresh) this.refreshNotifications();
    });

    this.subscriptions.push(sub);
  }

  markNotification(id: Guid) {
    const notification = this.notificationsListAUX.find((n) => n.id === id);
    if (!notification) return;

    const mark$ = notification.status === NotificationStatusEnum.Sent
      ? this.notificationSrv.markAsRead(id)
      : this.notificationSrv.markAsUnread(id);

    const sub = mark$.subscribe(() => {
      notification.status = notification.status === NotificationStatusEnum.Sent
        ? NotificationStatusEnum.Read
        : NotificationStatusEnum.Sent;

      this.notificationsCount = this.notificationsListAUX.filter((n) => n.status === NotificationStatusEnum.Sent).length;
      this.applyFilterToView();

    }, (err) => {
      console.error('Error marcando notificación', err);
    });

    this.subscriptions.push(sub);
  }

  goSupplierDetail(id: string) {
    this.router.navigate(['/supplier'], { queryParams: { id } });
  }

  goToOADetails(assemblyOrderId: string) {
    this.router.navigate(['/ManufacturingOrder'], {
      queryParams: { assemblyOrderId },
    });
  }

  goToSapArticleDetails(manufacturingItemId: string) {
    this.router.navigate(['/SapArticles'], {
      queryParams: { manufacturingItemId },
    });
  }

  goToBudget(budgetId: string) {
    this.router.navigate(['/budget'], {
      queryParams: { id: budgetId },
    });
  }

  goToTaskDetails(taskId: string) {
    this.router.navigate(['/scheduleTasks'], { queryParams: { taskId } });
  }

  goToNotificationCenter() {
    this.launchNotificationCenterEvent.emit();
    this.toggleDrawer();
  }

  toggleDrawer() {
    this.toggleNotificationSidebar = !this.toggleNotificationSidebar;
    if (this.toggleNotificationSidebar) this.openNotifiDrawerEvent.emit();
  }

  trackByNotificationId(index: number, item: NotificationModel) {
    return item.id;
  }

  showOnlyUnread(toggleChangeEvent: MatSlideToggleChange) {
    this.isChecked = toggleChangeEvent.checked;
    this.applyFilterToView();
  }
}
